/**
 * Notify Me Button — custom element that subscribes a customer's email
 * to Swym Back-in-Stock alerts for the currently selected variant.
 *
 * Variant changes are handled by product-form-component, which syncs
 * this element's host attributes from the server-rendered version so
 * `data-variant-id` and the `hidden` attribute stay in sync without
 * any listeners here.
 */

const SWYM_BACK_IN_STOCK_TYPE = 4;
const SWYM_BACK_IN_STOCK_TOPIC = 'backinstock';
const SWYM_READY_TIMEOUT_MS = 8000;
const TRIGGER_LABEL_DEFAULT = 'Notify me when available';
const TRIGGER_LABEL_SUBSCRIBED = "You're on the list";
const SUCCESS_TOAST_DURATION_MS = 2500;

class NotifyMeButton extends HTMLElement {
  static get observedAttributes() {
    return ['data-variant-id'];
  }

  constructor() {
    super();
    /** @type {HTMLDialogElement | null} */
    this.dialog = null;
    /** @type {HTMLElement | null} */
    this.form = null;
    /** @type {HTMLInputElement | null} */
    this.emailInput = null;
    /** @type {HTMLInputElement | null} */
    this.optInInput = null;
    /** @type {HTMLElement | null} */
    this.errorEl = null;
    /** @type {HTMLElement | null} */
    this.successEl = null;
    /** @type {HTMLButtonElement | null} */
    this.trigger = null;
    /** @type {HTMLButtonElement | null} */
    this.submitBtn = null;
    /** @type {HTMLButtonElement | null} */
    this.closeBtn = null;
    /** @type {Element | null} */
    this.lastFocused = null;
    /** @type {Array<{empi: number | string, epi: number | string}> | null} */
    this.subscribedProducts = null;
    /** @type {ReturnType<typeof setTimeout> | null} */
    this.toastTimer = null;
    this.handleBackdropClick = this.handleBackdropClick.bind(this);
    this.handleCancel = this.handleCancel.bind(this);
    this.handleEmailKeyDown = this.handleEmailKeyDown.bind(this);
    this.swymCallbackFn = this.swymCallbackFn.bind(this);
    this.fetchCallbackFn = this.fetchCallbackFn.bind(this);
  }

  connectedCallback() {
    this.trigger = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-notify-trigger]'));
    this.dialog = /** @type {HTMLDialogElement | null} */ (this.querySelector('[data-notify-dialog]'));
    this.form = /** @type {HTMLElement | null} */ (this.querySelector('[data-notify-form]'));
    this.emailInput = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-notify-email]'));
    this.optInInput = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-notify-optin]'));
    this.errorEl = /** @type {HTMLElement | null} */ (this.querySelector('[data-notify-error]'));
    this.successEl = /** @type {HTMLElement | null} */ (this.querySelector('[data-notify-success]'));
    this.submitBtn = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-notify-submit]'));
    this.closeBtn = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-notify-close]'));

    if (!this.trigger || !this.dialog || !this.submitBtn) return;

    this.trigger.addEventListener('click', () => this.open());
    this.closeBtn?.addEventListener('click', () => this.close());
    this.submitBtn.addEventListener('click', (e) => this.handleSubmit(e));
    this.emailInput?.addEventListener('keydown', this.handleEmailKeyDown);
    this.dialog.addEventListener('click', this.handleBackdropClick);
    this.dialog.addEventListener('cancel', this.handleCancel);

    const win = /** @type {any} */ (window);
    if (!win.SwymCallbacks) win.SwymCallbacks = [];
    win.SwymCallbacks.push(this.swymCallbackFn);
  }

  /**
   * @param {string} name
   * @param {string | null} oldValue
   * @param {string | null} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (name !== 'data-variant-id' || oldValue === newValue) return;
    if (this.subscribedProducts) {
      this.applySubscribedState();
    } else {
      const win = /** @type {any} */ (window);
      if (win._swat) this.swymCallbackFn();
    }
  }

  /**
   * Runs once Swym is ready. Fetches all back-in-stock subscriptions for
   * this customer/session and caches them so subsequent variant changes
   * can be resolved without re-hitting Swym.
   */
  swymCallbackFn() {
    const win = /** @type {any} */ (window);
    const swat = win._swat;
    if (typeof swat?.fetchSubscriptions !== 'function') return;
    swat.fetchSubscriptions(
      { topic: SWYM_BACK_IN_STOCK_TOPIC },
      this.fetchCallbackFn,
      (/** @type {unknown} */ err) => console.error('[notify-me] fetchSubscriptions failed', err)
    );
  }

  /**
   * @param {{ subscriptions?: Array<{empi: number | string, epi: number | string}> } | undefined} response
   */
  fetchCallbackFn(response) {
    const subscriptions = response?.subscriptions;
    if (!Array.isArray(subscriptions)) return;
    this.subscribedProducts = subscriptions;
    this.applySubscribedState();
  }

  applySubscribedState() {
    if (!this.trigger || !this.subscribedProducts) return;
    const productId = Number(this.dataset.productId);
    const variantId = Number(this.dataset.variantId);
    const isSubscribed = this.subscribedProducts.some(
      (p) => Number(p.empi) === productId && Number(p.epi) === variantId
    );
    if (isSubscribed) {
      this.markSubscribed();
    } else {
      this.resetTrigger();
    }
  }

  markSubscribed() {
    if (!this.trigger) return;
    this.trigger.setAttribute('disabled', 'true');
    this.trigger.textContent = TRIGGER_LABEL_SUBSCRIBED;
  }

  resetTrigger() {
    if (!this.trigger) return;
    this.trigger.removeAttribute('disabled');
    this.trigger.textContent = TRIGGER_LABEL_DEFAULT;
  }

  /**
   * @param {KeyboardEvent} event
   */
  handleEmailKeyDown(event) {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.handleSubmit(event);
    }
  }

  /**
   * @param {MouseEvent} event
   */
  handleBackdropClick(event) {
    if (event.target === this.dialog) this.close();
  }

  /**
   * @param {Event} event
   */
  handleCancel(event) {
    event.preventDefault();
    this.close();
  }

  open() {
    if (!this.dialog) return;
    this.resetMessages();
    this.lastFocused = document.activeElement;
    if (typeof this.dialog.showModal === 'function') {
      this.dialog.showModal();
    } else {
      this.dialog.setAttribute('open', '');
    }
    setTimeout(() => this.emailInput?.focus(), 0);
  }

  close() {
    if (!this.dialog) return;
    if (this.dialog.open) {
      this.dialog.close();
    } else {
      this.dialog.removeAttribute('open');
    }
    if (this.lastFocused instanceof HTMLElement) {
      this.lastFocused.focus();
    }
  }

  resetMessages() {
    this.errorEl?.classList.add('hidden');
    if (this.errorEl) this.errorEl.textContent = '';
  }

  showSuccessToast() {
    if (!this.successEl) return;
    if (this.toastTimer) clearTimeout(this.toastTimer);
    this.successEl.classList.remove('hidden');
    // Force reflow so the opacity transition runs from 0 → 1.
    void this.successEl.offsetWidth;
    this.successEl.classList.add('is-visible');
    this.toastTimer = setTimeout(() => {
      this.successEl?.classList.remove('is-visible');
      this.toastTimer = setTimeout(() => {
        this.successEl?.classList.add('hidden');
        this.toastTimer = null;
      }, 300);
    }, SUCCESS_TOAST_DURATION_MS);
  }

  /**
   * @param {string} message
   */
  showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message;
    this.errorEl.classList.remove('hidden');
  }

  /**
   * @param {Event} event
   */
  async handleSubmit(event) {
    event.preventDefault();
    this.resetMessages();

    if (!this.emailInput) return;
    const email = this.emailInput.value.trim();
    if (!email || !this.emailInput.checkValidity()) {
      this.emailInput.reportValidity();
      this.showError('Please enter a valid email address.');
      this.emailInput.focus();
      return;
    }

    const { productId, variantId, productUrl, productPrice, productImage } = this.dataset;
    if (!productId || !variantId || !productUrl) {
      this.showError('Missing product information.');
      return;
    }

    this.form?.classList.add('is-loading');

    try {
      await this.waitForSwymReady();

      const win = /** @type {any} */ (window);
      const swat = win._swat;
      if (typeof swat?.subscribeForProductAlert !== 'function') {
        throw new Error('Swym subscribeForProductAlert function not found on _swat');
      }

      const product = {
        empi: Number(productId),
        epi: Number(variantId),
        du: productUrl,
        pr: productPrice ? Number(productPrice) : 0,
        iu: productImage || '',
      };
      const addToMailingList = this.optInInput?.checked ? 1 : 0;

      await /** @type {Promise<unknown>} */ (
        new Promise((resolve, reject) => {
          try {
            swat.subscribeForProductAlert(
              email,
              'email',
              product,
              resolve,
              reject,
              addToMailingList,
              SWYM_BACK_IN_STOCK_TOPIC
            );
          } catch (err) {
            reject(err);
          }
        })
      );

      if (this.emailInput) this.emailInput.value = '';
      if (this.optInInput) this.optInInput.checked = false;
      if (this.subscribedProducts) {
        this.subscribedProducts.push({ empi: Number(productId), epi: Number(variantId) });
      }
      this.close();
      this.markSubscribed();
      this.showSuccessToast();
    } catch (err) {
      console.error('[notify-me] Swym subscribe failed', err);
      this.showError('Something went wrong. Please try again.');
    } finally {
      this.form?.classList.remove('is-loading');
    }
  }

  /**
   * @returns {Promise<void>}
   */
  waitForSwymReady() {
    return new Promise((resolve, reject) => {
      const win = /** @type {any} */ (window);
      if (win._swat) {
        resolve();
        return;
      }
      const timeout = setTimeout(
        () => reject(new Error('Swym did not load in time')),
        SWYM_READY_TIMEOUT_MS
      );
      win.SwymCallbacks = win.SwymCallbacks || [];
      win.SwymCallbacks.push(() => {
        clearTimeout(timeout);
        resolve();
      });
    });
  }
}

if (!customElements.get('notify-me-button')) {
  customElements.define('notify-me-button', NotifyMeButton);
}
